import { Checkbox } from '@ark-ui/react/checkbox';
import { type Finding } from '@consigli/types';
import clsx from 'clsx';
import { ChevronRight } from 'lucide-react';
import { type FC, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AddContextCheckboxControl } from './add-context-checkbox';
import { requirementPayload } from './response-requirements';
import { useFindingMessage } from './use-finding-message';

const ICON_STROKE_WIDTH = 1.75;

interface AddContextRequirementCardProps {
  finding: Finding;
  /** Without it the card is read-only: no checkbox gutter, only the items to open. */
  onSelectedChange?: (selected: boolean) => void;
  selected?: boolean;
}

/**
 * One response-requirement category, which is one finding: the category, what
 * it asks for, and its items once opened. Ticking it attaches the whole
 * category, which is exactly what the card shows.
 *
 * The two actions have a zone each so neither is mistaken for the other: the
 * checkbox owns a divided gutter on the left, which is its label and so a
 * full-height target, and the rest of the card is one button that opens the
 * items. The gutter's checkbox and the title share the title's first-line box,
 * so they sit on one line however long the description runs.
 */
export const AddContextRequirementCard: FC<AddContextRequirementCardProps> = ({
  finding,
  onSelectedChange,
  selected = false,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const itemsId = useId();
  const payload = requirementPayload(finding);
  // The finding's translated headline stands in when the payload does not
  // parse, which is the text its chip shows once attached.
  const headline = useFindingMessage(finding);
  const category = payload?.category ?? headline;
  const deliverables = payload?.deliverables ?? [];
  const hasItems = deliverables.length > 0;
  // Two items can share a name and a source, so a repeat is numbered by how
  // many came before it rather than by its place in the list.
  const seenKeys = new Map<string, number>();
  const itemKeys = deliverables.map((deliverable) => {
    const base = `${deliverable.name}-${deliverable.source ?? deliverable.description}`;
    const repeats = seenKeys.get(base) ?? 0;
    seenKeys.set(base, repeats + 1);
    return repeats ? `${base}-${repeats}` : base;
  });

  return (
    <div
      className={clsx(
        'grid w-full shrink-0 overflow-hidden rounded-[var(--ds-radius-16)] border border-solid',
        onSelectedChange && 'grid-cols-[var(--ds-space-14)_minmax(0,1fr)]',
        selected
          ? 'border-[var(--ds-color-raw-neutral-300)] bg-[var(--ds-color-bg-brand-lighter)]'
          : 'border-[var(--ds-color-border-primary)] bg-[var(--ds-color-bg-primary)] hover:bg-[var(--ds-color-bg-secondary)]',
      )}
    >
      {onSelectedChange && (
        <Checkbox.Root
          checked={selected}
          onCheckedChange={({ checked }) => onSelectedChange(checked === true)}
          className={clsx(
            // 24px of card padding plus 4px to centre the 16px box on the title's 24px line.
            'flex cursor-pointer items-start justify-center border-r border-solid pt-[calc(var(--ds-space-6)+var(--ds-space-1))]',
            selected
              ? 'border-[var(--ds-color-raw-neutral-300)]'
              : 'border-[var(--ds-color-border-primary)]',
          )}
        >
          <Checkbox.HiddenInput />
          <AddContextCheckboxControl />
          <Checkbox.Label className="sr-only">{category}</Checkbox.Label>
        </Checkbox.Root>
      )}

      <div className="min-w-0">
        <button
          type="button"
          // Only a card with items to open is an expander, and it points at
          // the list only while the list is there.
          aria-expanded={hasItems ? isOpen : undefined}
          aria-controls={hasItems && isOpen ? itemsId : undefined}
          disabled={!hasItems}
          onClick={() => setIsOpen((open) => !open)}
          className="add-context-expand grid w-full cursor-pointer grid-cols-[minmax(0,1fr)_auto] items-start gap-x-[var(--ds-space-4)] p-[var(--ds-space-6)] text-left disabled:cursor-default"
        >
          <span className="flex min-w-0 flex-col gap-[var(--ds-space-1)]">
            <span className="wrap-break-word text-[length:var(--ds-typography-heading-5-font-size)] font-[var(--ds-typography-paragraph-paragraph-medium-weight)] leading-[var(--ds-typography-heading-5-line-height)] text-[var(--ds-color-text-primary)]">
              {category}
            </span>
            {payload?.categoryDescription && (
              <span className="text-[length:var(--ds-typography-paragraph-small-font-size)] leading-[var(--ds-typography-paragraph-small-line-height)] text-[var(--ds-color-text-secondary)]">
                {payload.categoryDescription}
              </span>
            )}
          </span>
          <span className="flex h-[var(--ds-typography-heading-5-line-height)] items-center gap-[var(--ds-space-2)]">
            <span className="add-context-label-pill flex items-center rounded-[var(--ds-radius-8)] bg-[var(--ds-color-bg-brand-light)] px-[var(--ds-space-2)] py-[var(--ds-space-1)] text-[length:var(--ds-typography-paragraph-mini-font-size)] leading-[var(--ds-typography-paragraph-mini-line-height)] whitespace-nowrap tabular-nums">
              {t('prop-chat.add-context-requirement-items', { count: deliverables.length })}
            </span>
            {hasItems && (
              <ChevronRight
                aria-hidden="true"
                strokeWidth={ICON_STROKE_WIDTH}
                className={clsx(
                  'size-[var(--ds-space-4)] shrink-0 text-[var(--ds-color-text-secondary)] transition-transform motion-reduce:transition-none',
                  isOpen && 'rotate-90',
                )}
              />
            )}
          </span>
        </button>

        {isOpen && (
          <ul
            id={itemsId}
            className="flex flex-col gap-[var(--ds-space-3)] px-[var(--ds-space-6)] pb-[var(--ds-space-6)]"
          >
            {deliverables.map((deliverable, index) => (
              <li
                key={itemKeys[index]}
                className="flex flex-col gap-[var(--ds-space-1)] rounded-[var(--ds-radius-8)] border border-solid border-[var(--ds-color-border-primary)] bg-[var(--ds-color-bg-primary)] p-[var(--ds-space-4)]"
              >
                <p className="text-[length:var(--ds-typography-paragraph-regular-font-size)] font-[var(--ds-typography-paragraph-paragraph-medium-weight)] leading-[var(--ds-typography-paragraph-regular-line-height)] text-[var(--ds-color-text-primary)]">
                  {deliverable.name}
                </p>
                <p className="text-[length:var(--ds-typography-paragraph-small-font-size)] leading-[var(--ds-typography-paragraph-small-line-height)] text-[var(--ds-color-text-secondary)]">
                  {deliverable.description}
                </p>
                {deliverable.source && (
                  <blockquote className="mt-[var(--ds-space-1)] border-l-2 border-solid border-[var(--ds-color-border-primary)] pl-[var(--ds-space-3)] text-[length:var(--ds-typography-paragraph-mini-font-size)] italic leading-[var(--ds-typography-paragraph-mini-line-height)] text-[var(--ds-color-text-secondary)]">
                    <q>{deliverable.source}</q>
                  </blockquote>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
